/**
 * 网络请求封装
 * 基于 @iap/kit-http 提供统一的请求接口
 */

import http, { getRequestHeaders } from "./http";
import type { HttpConfig, HttpResponse } from "./http";
import LogUtil from "@/betby/utils/LogUtil";

// ==================== 类型定义 ====================

/**
 * 请求配置扩展（基于 HttpConfig）
 */
export interface RequestConfig extends Omit<HttpConfig, "url"> {
  /** 是否跳过默认错误处理 */
  skipErrorHandler?: boolean;
  /** 是否跳过 token 注入 */
  skipAuth?: boolean;
  /** 自定义请求参数（GET 请求的查询参数） */
  params?: Record<string, string | number | boolean | null | undefined>;
}

/**
 * API 响应格式（兼容 kit-http 的 HttpResponse）
 */
export type ApiResponse<T = unknown> = HttpResponse<T>;

/**
 * 请求错误类型
 */
export interface RequestError extends Error {
  /** 错误状态码 */
  status?: number;
  /** 错误响应数据 */
  response?: unknown;
  /** 错误类型 */
  type?: "network" | "timeout" | "abort" | "parse" | "http";
  /** 请求 URL */
  url?: string;
  /** 请求配置 */
  config?: RequestConfig;
}

// ==================== 工具函数 ====================

/**
 * 构建查询参数字符串
 * @param params 查询参数对象
 * @returns 查询参数字符串
 */
function buildQueryString(
  params?: Record<string, string | number | boolean | null | undefined>,
): string {
  if (!params || Object.keys(params).length === 0) {
    return "";
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
}

/**
 * 创建请求错误对象
 * @param error 原始错误
 * @param url 请求 URL
 * @param config 请求配置
 * @returns RequestError 对象
 */
function createRequestError(
  error: unknown,
  url: string,
  config: RequestConfig,
): RequestError {
  const requestError = error as RequestError;
  requestError.url = url;
  requestError.config = config;

  // 如果错误对象没有 message，设置默认消息
  if (!requestError.message) {
    requestError.message = `请求失败: ${url}`;
  }

  return requestError;
}

// ==================== 核心请求函数 ====================

/**
 * 通用请求方法
 * @param url 请求 URL
 * @param config 请求配置
 * @returns Promise<ApiResponse<T>>
 */
async function request<T = unknown>(
  url: string,
  config: RequestConfig = {},
): Promise<ApiResponse<T>> {
  const {
    skipErrorHandler = false,
    skipAuth = false,
    params,
    ...restConfig
  } = config;

  try {
    // 构建完整 URL（包含查询参数）
    const queryString = buildQueryString(params);
    const fullUrl = queryString ? `${url}${queryString}` : url;

    // 合并请求头（始终添加默认请求头，根据 skipAuth 决定是否添加 Authorization）
    LogUtil.info(
      `[API Request] ${restConfig.method || "GET"} ${fullUrl}`,
      skipAuth ? "(skipAuth)" : "",
    );
    const headers = getRequestHeaders(restConfig.headers, skipAuth);

    // 构建 HttpConfig
    const httpConfig: HttpConfig = {
      url: fullUrl,
      method: restConfig.method || "GET",
      ...(restConfig.body !== undefined && { body: restConfig.body }),
      ...(headers && { headers }),
      ...(restConfig.timeout && { timeout: restConfig.timeout }),
      ...(restConfig.baseUrl && { baseUrl: restConfig.baseUrl }),
      encryptionType: restConfig.encryptionType || "RSA",
      isToken: !skipAuth,
      ...(restConfig.throttle !== undefined && {
        throttle: restConfig.throttle,
      }),
      isSerErrTips: skipErrorHandler
        ? false
        : (restConfig.isSerErrTips ?? true),
      ...(restConfig.options && { options: restConfig.options }),
    };

    LogUtil.debug("[API Request] Config:", {
      method: httpConfig.method,
      url: httpConfig.url,
      hasToken: httpConfig.isToken,
      hasHeaders: !!httpConfig.headers,
    });

    // 使用 kit-http 发起请求
    const response = await http.comRequest<T>(httpConfig);

    LogUtil.success(
      `[API Response] ${restConfig.method || "GET"} ${fullUrl}`,
      response.code === 200 ? "✅" : "❌",
      response.code,
    );

    // 返回响应
    return response;
  } catch (error) {
    const requestError = createRequestError(error, url, config);

    // 打印错误信息
    LogUtil.error("❌ [API Error] 请求失败:", {
      url,
      method: restConfig.method || "GET",
      error: {
        message: requestError.message,
        status: requestError.status,
        type: requestError.type,
        response: requestError.response,
      },
    });

    // 错误处理
    if (!skipErrorHandler) {
      // 可以在这里添加全局错误处理逻辑
      // 例如：上报错误到监控系统
    }

    throw requestError;
  }
}

// ==================== 便捷方法 ====================

/**
 * GET 请求
 * @param url 请求 URL
 * @param config 请求配置
 * @returns Promise<ApiResponse<T>>
 */
export function get<T = unknown>(
  url: string,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    ...config,
    method: "GET",
  });
}

/**
 * POST 请求
 * @param url 请求 URL
 * @param data 请求体数据
 * @param config 请求配置
 * @returns Promise<ApiResponse<T>>
 */
export function post<T = unknown>(
  url: string,
  data?: unknown,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    ...config,
    method: "POST",
    body: data,
  });
}

/**
 * PUT 请求
 * @param url 请求 URL
 * @param data 请求体数据
 * @param config 请求配置
 * @returns Promise<ApiResponse<T>>
 */
export function put<T = unknown>(
  url: string,
  data?: unknown,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    ...config,
    method: "PUT",
    body: data,
  });
}

/**
 * PATCH 请求
 * @param url 请求 URL
 * @param data 请求体数据
 * @param config 请求配置
 * @returns Promise<ApiResponse<T>>
 */
export function patch<T = unknown>(
  url: string,
  data?: unknown,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    ...config,
    method: "PATCH",
    body: data,
  });
}

/**
 * DELETE 请求
 * @param url 请求 URL
 * @param config 请求配置
 * @returns Promise<ApiResponse<T>>
 */
export function del<T = unknown>(
  url: string,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    ...config,
    method: "DELETE",
  });
}

// ==================== 导出 ====================

const api = {
  get,
  post,
  put,
  patch,
  delete: del,
  request,
};

export default api;
